import * as THREE from 'three';
import { sunDirectionFromAzimuthElevation } from './sunMath';
import { DEFAULT_LIGHTING_CONFIG, type LightingConfig } from './types';

/**
 * Owns the actual THREE.Light objects and knows how to apply a
 * LightingConfig to them. This is Engine-level infrastructure: it has no
 * idea what a "course" or "hole" is, only how to turn lighting *data* into
 * a correctly configured shadow-casting sun + ambient fill.
 *
 * Course supplies the data (LightingConfig); Engine supplies the mechanism
 * (this class).
 *
 * Shadow architecture note: this currently uses a single directional-light
 * shadow map sized to `shadowFrustumSize`, which is fine for a single hole
 * in view but will show resolution limits across an 18-hole course seen at
 * a distance (e.g. a flyover camera). The upgrade path is Cascaded Shadow
 * Maps — three ships an official implementation at
 * `three/examples/jsm/csm/CSM.js` that slices the view frustum into
 * several shadow cameras at increasing distance. We're not wiring it in
 * yet because CSM needs a live camera reference (it rebuilds its cascades
 * from the active camera's frustum every frame) and CameraManager's active
 * camera can currently change at runtime — the integration point once
 * that's worth doing is here, replacing `sun`/`sun.shadow` with a CSM
 * instance driven by `engine.cameras.activeCamera`, keeping this class's
 * public API (`applyConfig`, `sunLight`) the same so nothing downstream
 * has to change.
 */
export class LightingSystem {
  private readonly sun: THREE.DirectionalLight;
  private readonly hemisphere: THREE.HemisphereLight;
  private readonly sunDistance = 100;

  constructor(private readonly scene: THREE.Scene) {
    this.sun = new THREE.DirectionalLight();
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = this.sunDistance * 3;
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.02;

    this.hemisphere = new THREE.HemisphereLight();

    scene.add(this.sun, this.sun.target, this.hemisphere);

    this.applyConfig(DEFAULT_LIGHTING_CONFIG);
  }

  get sunLight(): THREE.DirectionalLight {
    return this.sun;
  }

  get hemisphereLight(): THREE.HemisphereLight {
    return this.hemisphere;
  }

  applyConfig(config: LightingConfig): void {
    const direction = sunDirectionFromAzimuthElevation(
      config.sunAzimuthDeg,
      config.sunElevationDeg,
    );

    this.sun.position.copy(direction).multiplyScalar(this.sunDistance);
    this.sun.target.position.set(0, 0, 0);
    this.sun.color.setHex(config.sunColor);
    this.sun.intensity = config.sunIntensity;

    const frustum = config.shadowFrustumSize;
    const cam = this.sun.shadow.camera as THREE.OrthographicCamera;
    cam.left = -frustum;
    cam.right = frustum;
    cam.top = frustum;
    cam.bottom = -frustum;
    cam.updateProjectionMatrix();

    this.hemisphere.color.setHex(config.hemisphereSkyColor);
    this.hemisphere.groundColor.setHex(config.hemisphereGroundColor);
    this.hemisphere.intensity = config.hemisphereIntensity;
  }

  dispose(): void {
    this.scene.remove(this.sun, this.sun.target, this.hemisphere);
    this.sun.dispose();
    this.hemisphere.dispose();
  }
}
